import React, { useEffect } from 'react';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import { TextField } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import FormHelperText from '@material-ui/core/FormHelperText';
import { Helmet } from 'react-helmet-async';

import {
	useSignupMutation,
	useAddImgMutation,
	useGetUserDetailsQuery,
	useUpdateUserMutation,
	useGetProfileAttachmentsQuery,
	useDeleteProfileAttachmentMutation,
} from '../../../store/apiSlice';
import { useForm, Controller } from 'react-hook-form';
import Messages from '../../../defs/messages';
import Const from '../../../defs/const';

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		alignItems: 'center',
	},
	appBarSpacer: theme.mixins.toolbar,
	content: {
		flexGrow: 1,
		height: '100vh',
		overflow: 'auto',
	},
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	paper: {
		padding: theme.spacing(2),
	},
	buttonStyle: {
		margin: theme.spacing(1),
	},
	input: {
		display: 'none',
	},
	title: {
		flexGrow: 1,
	},
	attachmentList: {
		marginTop: theme.spacing(2),
	},
}));

function getOrganizations() {
	let orgs = [];
	for (let i = 0; i < Const.ORGANIZATIONS.length; ++i) {
		orgs.push({
			value: i + '',
			label: Const.ORGANIZATIONS[i],
		});
	}
	orgs[0].selected = true;
	return orgs;
}

function normalizeCheckboxValue(value) {
	return value === true || value === 'true' || value === 1 || value === '1';
}

export function UserEdit() {
	const classes = useStyles();
	const msg = Messages;
	const navigate = useNavigate();
	const { id } = useParams();

	// RTK Query hooks
	const { data: user } = useGetUserDetailsQuery({ id: +id });
	const [updateUser] = useUpdateUserMutation();
	const { data: attachments, refetch: refetchAttachments } =
		useGetProfileAttachmentsQuery(id);
	const [deleteAttachment] = useDeleteProfileAttachmentMutation();

	const {
		handleSubmit,
		control,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm({
		defaultValues: {
			username: '',
			organization_id: '',
			TELKs: null, // file input field for TELK file
			email: '',
			first_name: '',
			last_name: '',
			code: '',
			id_card: '',
			is_superuser: false,
			is_staff: false,
			status: '',
			lost_vision: 0,
			message: '',
			new_password: '',
		},
	});

	useEffect(() => {
		if (user) {
			reset({
				username: user.username,
				organization_id: user.tbl_profile?.organization_id,
				TELKs: null,
				email: user.email,
				first_name: user.first_name,
				last_name: user.last_name,
				code: user.tbl_profile?.code,
				id_card: user.tbl_profile?.id_card,
				lost_vision: user.tbl_profile?.lost_vision,
				is_superuser: normalizeCheckboxValue(user.is_superuser),
				is_staff: normalizeCheckboxValue(user.is_staff),
				status: user.tbl_profile?.status,
				message: '',
				new_password: '',
			});
		}
	}, [user, reset]);

	const onSubmit = async (values) => {
		// Build FormData for multipart/form-data submission.
		const formData = new FormData();
		formData.append('id', id);
		formData.append('username', values.username);
		formData.append('organization_id', values.organization_id);
		formData.append('email', values.email);
		formData.append('first_name', values.first_name);
		formData.append('last_name', values.last_name);
		formData.append('code', values.code);
		formData.append('id_card', values.id_card);
		formData.append('lost_vision', values.lost_vision);
		formData.append('is_superuser', values.is_superuser);
		formData.append('is_staff', values.is_staff);
		formData.append('status', values.status);
		formData.append('message', values.message);

		// Append new password only when the admin has entered one
		if (values.new_password && values.new_password.trim().length > 0) {
			formData.append('new_password', values.new_password.trim());
		}

		// Append TELK file(s) if selected, under the key "TELKs"
		if (values.TELKs && values.TELKs.length > 0) {
			for (let i = 0; i < values.TELKs.length; i++) {
				formData.append('TELKs', values.TELKs[i]);
			}
		}

		try {
			await updateUser(formData).unwrap();
			// Refetch attachments after update if needed.
			refetchAttachments();
			navigate(`/admin/users/${user.id}`);
		} catch (err) {
			console.error('Error updating user:', err);
		}
	};

	const handleDeleteAttachment = async (attachmentId) => {
		try {
			await deleteAttachment(attachmentId).unwrap();
			refetchAttachments();
		} catch (err) {
			console.error('Error deleting attachment:', err);
		}
	};

	return (
		<div align="center" className={classes.root}>
			<CssBaseline />
			<Helmet>
				<title>Редакция на потребител</title>
			</Helmet>
			<Container maxWidth="lg" className={classes.container}>
				<Paper className={classes.paper}>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Grid
							container
							spacing={1}
							justifyContent="flex-start"
							alignItems="flex-start"
							direction="row"
						>
							<Grid item xs={12}>
								<Typography
									component="h1"
									variant="h4"
									color="inherit"
									noWrap
									className={classes.title}
									role="alert"
								>
									{msg.REG.HEADER_MESSAGE}
								</Typography>
								<hr />
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="username"
									control={control}
									rules={{
										required: {
											value: true,
											message: msg.REG.FIELD_REQUIRED,
										},
									}}
									render={({ field }) => (
										<TextField
											label="Потребител"
											error={!!errors.username}
											helperText={errors?.username?.message}
											id="username"
											{...field}
											style={{ width: '90%', margin: 20 }}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="first_name"
									control={control}
									rules={{
										required: {
											value: true,
											message: msg.ERROR_FNAME_EMPTY,
										},
									}}
									render={({ field }) => (
										<TextField
											label="Име"
											type="text"
											error={!!errors.first_name}
											helperText={errors?.first_name?.message}
											id="first_name"
											{...field}
											style={{ width: '90%', margin: 20 }}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="last_name"
									control={control}
									rules={{
										required: {
											value: true,
											message: msg.ERROR_LNAME_EMPTY,
										},
									}}
									render={({ field }) => (
										<TextField
											style={{ width: '90%', margin: 20 }}
											label="Фамилия"
											type="text"
											error={!!errors.last_name}
											helperText={errors?.last_name?.message}
											id="last_name"
											{...field}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="email"
									control={control}
									rules={{
										required: {
											value: true,
											message: msg.ERROR_EMAIL_EMPTY,
										},
										pattern: {
											value: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
											message: msg.ERROR_EMAIL_INVALID,
										},
									}}
									render={({ field }) => (
										<TextField
											label="Имейл"
											type="text"
											error={!!errors.email}
											helperText={errors?.email?.message}
											id="email"
											{...field}
											style={{ width: '90%', margin: 20 }}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="organization_id"
									control={control}
									rules={{
										required: {
											value: true,
											message: msg.ERROR_ORGANIZATION_EMPTY,
										},
									}}
									render={({ field }) => (
										<TextField
											label="Организация"
											select
											variant="standard"
											margin="normal"
											InputLabelProps={{
												shrink: true,
											}}
											error={!!errors.organization_id}
											helperText={errors?.organization_id?.message}
											id="organization_id"
											{...field}
											style={{ width: '90%', margin: 20 }}
										>
											{getOrganizations().map((option) => (
												<MenuItem key={option.value} value={option.value}>
													{option.label}
												</MenuItem>
											))}
										</TextField>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="code"
									control={control}
									rules={{
										required: {
											value: true,
											message: msg.ERROR_EGN_EMPTY,
										},
										validate: (value) =>
											(value.trim().length === value.length &&
												!isNaN(value) &&
												+value > 0) ||
											'Моля въведете валидно ЕГН.',
									}}
									render={({ field }) => (
										<TextField
											label="ЕГН"
											type="text"
											error={!!errors.code}
											helperText={errors?.code?.message}
											id="code"
											{...field}
											style={{ width: '90%', margin: 20 }}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="id_card"
									control={control}
									rules={{
										required: {
											value: true,
											message: msg.ERROR_ADDRESS_EMPTY,
										},
									}}
									render={({ field }) => (
										<TextField
											label="Адрес"
											type="text"
											error={!!errors.id_card}
											helperText={errors?.id_card?.message}
											id="id_card"
											{...field}
											style={{ width: '90%', margin: 20 }}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="lost_vision"
									control={control}
									rules={{
										required: {
											value: true,
											message: msg.ERROR_PERCENT_EMPTY,
										},
										validate: (value) => {
											return (
												(Number.isInteger(value)
													? value
													: value.trim().length === value.length &&
													  !isNaN(value) &&
													  +value >= 70) ||
												'Моля въведете валиден процент загубено зрение.'
											);
										},
									}}
									render={({ field }) => (
										<TextField
											label="Процент загубено зрение"
											type="text"
											error={!!errors.lost_vision}
											helperText={errors?.lost_vision?.message}
											id="lost_vision"
											{...field}
											style={{ width: '90%', margin: 20 }}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="is_superuser"
									control={control}
									render={({ field }) => (
										<>
											<FormControlLabel
												control={
													<Checkbox
														color="primary"
														name={field.name}
														id="is_superuser"
														checked={normalizeCheckboxValue(field.value)}
														onChange={(event) =>
															field.onChange(event.target.checked)
														}
														onBlur={field.onBlur}
														inputRef={field.ref}
													/>
												}
												label="Администратор"
											/>
											<Typography variant="subtitle2">
												Определя дали потребителя има всички права в системата.
											</Typography>
										</>
									)}
								/>
							</Grid>
								<Grid item xs={12}>
									<Controller
										name="is_staff"
										control={control}
										render={({ field }) => (
											<>
												<FormControlLabel
													control={
														<Checkbox
															color="primary"
															name={field.name}
															id="is_staff"
															checked={normalizeCheckboxValue(field.value)}
															onChange={(event) =>
																field.onChange(event.target.checked)
															}
															onBlur={field.onBlur}
															inputRef={field.ref}
														/>
													}
													label="Служител в РСО"
												/>
												<Typography variant="subtitle2">
													Определя дали потребителя може да сваля неограничен брой
													книги.
												</Typography>
											</>
										)}
									/>
								</Grid>
								<Grid item xs={12}>
									<Controller
										name="status"
										control={control}
										render={({ field }) => (
											<>
												<InputLabel id="status-label">Статус</InputLabel>
												<Select
													style={{ width: '90%', margin: 20 }}
													labelId="status-label"
													id="status"
													{...field}
												>
													<MenuItem value={''}></MenuItem>
													<MenuItem value={Const.STATUS_PENDING}>Нов</MenuItem>
													<MenuItem value={Const.STATUS_ENABLED}>
														Одобрен
													</MenuItem>
													<MenuItem value={Const.STATUS_REJECTED}>
														Отхвърлен
													</MenuItem>
													<MenuItem value={Const.STATUS_DISABLED}>
														Неактивен
													</MenuItem>
												</Select>
											</>
										)}
									/>
								</Grid>
								<Grid item xs={12}>
									<Controller
										name="message"
										control={control}
										render={({ field }) => (
											<TextField
												label="Съобщение"
												type="text"
												id="message"
												{...field}
												style={{ width: '90%', margin: 20 }}
											/>
										)}
									/>
								</Grid>
							{/* Optional password reset field */}
							<Grid item xs={12}>
								<Controller
									name="new_password"
									control={control}
									rules={{
										validate: (value) => {
											if (!value || value.trim().length === 0) return true;
											if (value.trim().length < 8)
												return 'Паролата трябва да е поне 8 символа.';
											return true;
										},
									}}
									render={({ field }) => (
										<TextField
											label="Нова парола (незадължително)"
											type="password"
											id="new_password"
											inputProps={{
												autoComplete: 'new-password',
												'aria-describedby': 'new-password-hint',
											}}
											error={!!errors.new_password}
											helperText={
												errors?.new_password?.message ||
												'Оставете празно, за да запазите текущата парола.'
											}
											{...field}
											style={{ width: '90%', margin: 20 }}
										/>
									)}
								/>
							</Grid>
							{/* TELK file upload field */}
							<Grid item xs={12} className={classes.mt2}>
								<Controller
									name="TELKs"
									control={control}
									render={({ field }) => (
										<input
											type="file"
											id="TELKs"
											multiple
											onChange={(e) => field.onChange(e.target.files)}
										/>
									)}
								/>
							</Grid>
							{/* List existing attachments */}
							<Grid item xs={12} className={classes.attachmentList}>
								<Typography variant="h6">Прикачени файлове</Typography>
								{attachments && attachments.length > 0 ? (
									attachments.map((att) => (
										<Chip
											key={att.id}
											label={att.name}
											onDelete={() => handleDeleteAttachment(att.id)}
											style={{ margin: '4px' }}
											color="primary"
										/>
									))
								) : (
									<Typography variant="body2">
										Няма прикачени файлове.
									</Typography>
								)}
							</Grid>
							<Grid item xs={12}>
								<Button
									key="Register"
									color="primary"
									variant="outlined"
									className={classes.buttonStyle}
									type="submit"
								>
									Запис
								</Button>
								<Link to={'/admin/users'}>
									<Button variant="outlined">Отказ</Button>
								</Link>
							</Grid>
						</Grid>
					</form>
				</Paper>
			</Container>
		</div>
	);
}
